import { Box, Paper, Stack, styled } from '@mui/material';
import PageHeader from '../../controls/PageHeader';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import TextSearch from '../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { RefObject, useMemo, useState } from 'react';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { DataGridPro } from '../../tables/DataGridPro';
import { AdminSettingsGroupCell } from './AdminSettingsGroupCell';
import { hasWordStartingWith } from '../../../utils/string.utils';
import {
  AdminSettingFormRef,
  AdminSettingsDialog,
} from './AdminSettingsDialog';
import { SingleCheckboxFieldForm } from './SingleCheckboxFieldForm';
import { SingleNumberInputFieldForm } from './SingleNumberInputFieldForm';

export interface AdminSetting {
  category: string;
  description?: string;
  value?: any;
  displayValue?: string;
  settingId?: AdminSettingId;
  children?: AdminSetting[];
  form?: (formRef: RefObject<AdminSettingFormRef>) => JSX.Element;
  saveHandler?: (values: any) => void;
}

export interface AdminSettingsRowItem {
  id: string;
  category: string;
  description?: string;
  value?: any;
  displayValue?: string;
  path: string[];
  settingId?: AdminSettingId;
  hasChild: boolean;
  form?: (formRef: RefObject<AdminSettingFormRef>) => JSX.Element;
  saveHandler?: (values: any) => void;
}

export interface AdminDialogProperty {
  settingId: AdminSettingId;
  title: string;
  description: string;
  form: (formRef: RefObject<AdminSettingFormRef>) => JSX.Element;
  saveHandler: (values: any) => void;
}

export enum AdminSettingId {
  RequiredTimeOnSite = 'requiredTimeOnSite',
  SMSMessaging = 'smsMessaging',
}

const PREFIX = 'AdminSettings';

const classes = {
  container: `${PREFIX}-container`,
  contentContainer: `${PREFIX}-contentContainer`,
  search: `${PREFIX}-search`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(1),
  },
  [`& .${classes.contentContainer}`]: {
    padding: theme.spacing(2),
  },
  [`& .${classes.search}`]: {
    width: 327,
    marginRight: theme.spacing(3),
  },
}));

interface StyledDataGridProProps {
  isEmpty?: boolean;
}

const StyledDataGridPro = styled(DataGridPro)<StyledDataGridProProps>(
  ({ theme, isEmpty }) => ({
    '& .MuiDataGrid-virtualScrollerContent': {
      width: '100% !important',
      maxWidth: '100% !important',
      height: isEmpty ? '100px !important' : 'auto',
    },
    '& .leaf-row': {
      cursor: 'pointer',
    },
    '& .category-header-cell .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: 22,
    },
    '& .value-cell': {
      paddingLeft: 10,
    },
    '& .row-depth-3 .category-cell': {
      marginRight: -50,
    },
    '& .row-depth-2, & .row-depth-3': {
      marginLeft: 50,
    },
  }),
);

export default function AdminSettings() {
  const t = useCommonTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogProperties, setDialogProperties] =
    useState<AdminDialogProperty | null>(null);

  const settings: AdminSetting[] = [
    {
      category: t('adminSettings.exceptionAndCommunication'),
      value: '',
      children: [
        {
          category: t('adminSettings.communicationPreferences'),
          value: '',
          children: [
            {
              category: t('adminSettings.smsMessaging'),
              description: t('adminSettings.smsMessagingDescription'),
              value: true,
              displayValue: t('adminSettings.enabled'),
              settingId: AdminSettingId.SMSMessaging,
              form: (formRef: RefObject<AdminSettingFormRef>) => {
                return (
                  <SingleCheckboxFieldForm
                    ref={formRef}
                    settingId={AdminSettingId.SMSMessaging}
                  />
                );
              },
              saveHandler: (values) => {
                //TODO
                console.log('saved values', values);
              },
            },
          ],
        },
      ],
    },
    {
      category: t('adminSettings.serviceDelivery'),
      value: '',
      children: [
        {
          category: t('adminSettings.performanceStandards'),
          value: '',
          children: [
            {
              category: t('adminSettings.requiredTimeOnSite'),
              description: t('adminSettings.requiredTimeOnSiteDescription'),
              value: 10,
              displayValue: `${10} ${t('adminSettings.minutes')}`,
              settingId: AdminSettingId.RequiredTimeOnSite,
              form: (formRef: RefObject<AdminSettingFormRef>) => {
                return (
                  <SingleNumberInputFieldForm
                    ref={formRef}
                    settingId={AdminSettingId.RequiredTimeOnSite}
                  />
                );
              },
              saveHandler: (values) => {
                //TODO
                console.log('saved values', values);
              },
            },
          ],
        },
      ],
    },
  ];

  const flattenSettings = (
    settings: AdminSetting[],
    parentPath: string[] = [],
  ): AdminSettingsRowItem[] => {
    return settings.flatMap((setting) => {
      const currentPath = [...parentPath, setting.category];

      const row: AdminSettingsRowItem = {
        id: currentPath.join('#'),
        category: setting.category,
        description: setting.description,
        value: setting.value,
        displayValue: setting.displayValue,
        settingId: setting.settingId,
        path: currentPath,
        hasChild: (setting.children && setting.children.length > 0) ?? false,
        form: setting.form,
        saveHandler: setting.saveHandler,
      };

      if (setting.children && setting.children.length > 0) {
        return [row, ...flattenSettings(setting.children, currentPath)];
      }

      return [row];
    });
  };

  const rows = flattenSettings(settings).filter(
    (row) => row.hasChild || hasWordStartingWith(row.category, searchText),
  );

  const getTreeDataPath = (row: AdminSettingsRowItem) => row.path;

  const groupingColDef = useMemo(
    () => ({
      hideDescendantCount: true,
      headerName: t('adminSettings.category'),
      headerClassName: 'category-header-cell',
      cellClassName: 'category-cell',
      renderCell: AdminSettingsGroupCell,
      minWidth: 500,
    }),
    [],
  );

  const columns: GridColDef[] = [
    {
      field: 'displayValue',
      headerName: t('adminSettings.value'),
      cellClassName: 'value-cell',
      sortable: false,
    },
  ];

  const onRowClick = (params: GridRowParams) => {
    if (params.row.hasChild) {
      return;
    }
    if (!params.row.settingId || !params.row.form) {
      return;
    }
    setDialogProperties({
      title: params.row.category,
      description: params.row.description,
      settingId: params.row.settingId,
      form: params.row.form,
      saveHandler: params.row.saveHandler,
    });
    setIsDialogOpen(true);
  };

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  return (
    <StyledPaper>
      <Box className={classes.container}>
        <PageHeader
          title={t('adminSettings.title')}
          returnLink="/settings"
          returnLinkText={t('settings.title')}
        />
        <Stack direction="column" className={classes.contentContainer}>
          <TextSearch
            id="text-search"
            className={classes.search}
            icon={<SearchIcon />}
            label={t('common.search')}
            currentSearchFilter={searchText}
            onSearchChange={handleSearchTextChange}
          />
          <StyledDataGridPro
            rows={rows}
            columns={columns}
            treeData
            getTreeDataPath={getTreeDataPath}
            defaultGroupingExpansionDepth={0}
            groupingColDef={groupingColDef}
            loading={isLoading}
            isEmpty={!rows || rows.length === 0}
            getRowClassName={(params) => {
              const depth = params.row.path.length;
              let classNames = `row-depth-${depth}`;
              if (!params.row.hasChild) {
                classNames = `${classNames} leaf-row`;
              }
              return classNames;
            }}
            getRowHeight={(params) => 56}
            onRowClick={onRowClick}
            disableColumnMenu={true}
            pagination
          />
        </Stack>
      </Box>
      {dialogProperties && (
        <AdminSettingsDialog
          open={isDialogOpen}
          settingsInfo={dialogProperties}
          handleClose={() => setIsDialogOpen(false)}
        />
      )}
    </StyledPaper>
  );
}
